/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { createContext, Dispatch, ReactNode, Ref, RefObject, SetStateAction, useContext, useEffect, useRef, useState } from "react";

const HeaderContext = createContext<{
  activeSection?: string | null;
  setActiveSection?: Dispatch<SetStateAction<string | null>>;
  mobileNavActive?: boolean;
  setMobileNavActive?: Dispatch<SetStateAction<boolean>>;
  setHeaderMobileCategorySelectSlot?: Dispatch<SetStateAction<HTMLDivElement | null>>;
  headerMobileCategorySelectSlot?: HTMLDivElement | null;
}>({});

export const useHeader = () => {
  return useContext(HeaderContext);
};

export type HeaderContextProviderProps = {
  children: ReactNode;
};

export default function HeaderContextProvider({ children }: HeaderContextProviderProps) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [mobileNavActive, setMobileNavActive] = useState<boolean>(false);
  const [headerMobileCategorySelectSlot, setHeaderMobileCategorySelectSlot] = useState<HTMLDivElement | null>(null);

  /* Closes any active section if Escape is pressed, for a11y */
  useEffect(() => {
    if (!activeSection && !mobileNavActive) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape") {
        setActiveSection(null);
        setMobileNavActive(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileNavActive, activeSection]);

  return (
    <HeaderContext.Provider
      value={{
        activeSection,
        setActiveSection,
        mobileNavActive,
        setMobileNavActive,
        headerMobileCategorySelectSlot,
        setHeaderMobileCategorySelectSlot,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
}
